import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FileDropzoneIcon, FileDropzoneText } from "./FileDropzone.styles";
import UploadIcon from "./assets/UploadIcon";
import { useNotification } from "@src/context/NotificationContext";
import { useFileUpload } from "@src/context/FileUploadContext";

interface FileDropWrapperProps {
  onDrop: (files: FileList) => void;
  uploading: boolean;
  progress: number;
  accept?: string;
  multiple?: boolean;
  filesCompletedCount?: number;
  filesCount?: number;
  children: React.ReactNode;
  onClick?: () => void;
  disableDrag?: boolean;
}

const DropOverlay = styled.div<{ isDragging: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isDragging }) => (isDragging ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 0 20px;
  color: white;
  font-size: 18px;
  transition: opacity 0.3s;
  user-select: none;
  border: 2px dashed ${({ theme }): string => theme.colors.yellow};
`;

const FileDropWrapper: React.FC<FileDropWrapperProps> = ({
  onDrop,
  children,
  onClick = () => {},
  accept,
  disableDrag = false,
}) => {
  const { uploading, setShowProgressModal, setValidatingFiles } =
    useFileUpload();
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const { addNotification } = useNotification();
  useEffect(() => {
    let dragCounter = 0;
    let dragTimeout: NodeJS.Timeout;

    const handleDragEnter = (event: DragEvent) => {
      if (disableDrag) return;
      event.preventDefault();
      dragCounter++;
      clearTimeout(dragTimeout);
      setIsDraggingFile(true);
    };

    const handleDragOver = (event: DragEvent) => {
      if (disableDrag) return;
      event.preventDefault();
      clearTimeout(dragTimeout);

      dragTimeout = setTimeout(() => {
        dragCounter = 0;
        setIsDraggingFile(false);
      }, 100);
    };

    const handleDragLeave = (event: DragEvent) => {
      if (disableDrag) return;
      event.preventDefault();
      dragCounter--;

      if (dragCounter <= 0) {
        dragTimeout = setTimeout(() => {
          setIsDraggingFile(false);
        }, 100);
      }
    };
    const isAudioFileValid = (file: File): Promise<boolean> => {
      return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = async (event) => {
          try {
            const audioContext = new AudioContext();
            const arrayBuffer = event.target?.result;
            if (!arrayBuffer) {
              resolve(false);
              return;
            }
            await audioContext.decodeAudioData(arrayBuffer as ArrayBuffer);
            resolve(true); // Decoded successfully
          } catch {
            resolve(false); // Failed to decode
          }
        };

        reader.onerror = () => {
          resolve(false); // Failed to read
        };

        reader.readAsArrayBuffer(file);
      });
    };
    // Helper: fallback validation using <audio> tag for .m4a support
    const isAudioPlayableViaTag = (file: File): Promise<boolean> => {
      return new Promise((resolve) => {
        const audio = document.createElement("audio");
        audio.preload = "metadata";

        audio.onloadedmetadata = () => {
          URL.revokeObjectURL(audio.src);
          resolve(true);
        };

        audio.onerror = () => {
          URL.revokeObjectURL(audio.src);
          resolve(false);
        };

        audio.src = URL.createObjectURL(file);
      });
    };

    const validateFiles = async (files: FileList) => {
      const validFiles: File[] = [];
      let invalidFilesCount = 0;

      for (const file of Array.from(files)) {
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        const isValidType = file.type.startsWith("audio/");
        if (!isValidType && fileExtension !== "m4a") {
          invalidFilesCount++;
          continue;
        }

        let isValidAudio = await isAudioFileValid(file);
        if (
          !isValidAudio &&
          (fileExtension === "m4a" || fileExtension === "aiff")
        ) {
          isValidAudio = await isAudioPlayableViaTag(file);
        }

        if (
          isValidAudio ||
          fileExtension === "m4a" ||
          fileExtension === "aiff"
        ) {
          validFiles.push(file);
        } else {
          invalidFilesCount++;
        }
      }

      return { validFiles, invalidFilesCount };
    };
    const handleDrop = async (event: DragEvent) => {
      event.preventDefault();
      clearTimeout(dragTimeout);
      dragCounter = 0;
      setIsDraggingFile(false);
      if (uploading) {
        addNotification(
          "Please wait until the current upload is complete.",
          "error"
        );
        return;
      }
      setShowProgressModal(true);
      setValidatingFiles(true);
      if (event.dataTransfer?.files.length) {
        if (accept) {
          const { validFiles, invalidFilesCount } = await validateFiles(
            event.dataTransfer.files
          );

          if (validFiles.length === 0) {
            addNotification(
              "Invalid or corrupted file(s). Please drop valid audio files only.",
              "error"
            );
            return;
          }

          if (invalidFilesCount > 0) {
            addNotification(
              "Some files were not accepted due to invalid format or corruption.",
              "warning"
            );
          }

          const dataTransfer = new DataTransfer();
          validFiles.forEach((file) => dataTransfer.items.add(file));
          setValidatingFiles(false);
          onDrop(dataTransfer.files);
        } else {
          setValidatingFiles(false);
          onDrop(event.dataTransfer.files);
        }
      }
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      clearTimeout(dragTimeout);
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [onDrop, accept]);

  const onClickOutside = () => {
    if (isDraggingFile) return;
    onClick();
  };
  return (
    <>
      <DropOverlay onClick={onClickOutside} isDragging={isDraggingFile}>
        <FileDropzoneIcon>
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <UploadIcon size={39} />
            <FileDropzoneText
              style={{
                fontSize: ".8rem",
                fontWeight: 400,
              }}
            >
              {"Drop anywhere"}
            </FileDropzoneText>
          </div>
        </FileDropzoneIcon>
      </DropOverlay>
      {children}
    </>
  );
};

export default FileDropWrapper;
